import { MeasurementsInterface, RoomDataInterface } from '@app/types/rooms';
import { ReportInterface } from '@app/types/buildings';
import { notification } from 'antd';
import dayjs from 'dayjs';
import { action, observable } from 'mobx';
import api from './api';
import { toStringTimeFormatter } from '@app/utils/helpers';

export default class ReportStore {
  public dubaiTZ: number = +4;
  @observable buildingId: number | null = null;
  @observable beginDate: number | null = null;
  @observable endDate: number | null = null;
  @observable loading: boolean = false;
  @observable selectedRoomId: string = '';
  @observable report: ReportInterface | null = null;
  @observable roomData: Partial<RoomDataInterface> | null = {};
  @observable actionLoading: boolean = false;
  @observable measurements: Array<MeasurementsInterface> = [];
  @observable isOneWeekGraphPeriod: boolean = true;

  // private measurementsTimeFormatter = (date: string) => {
  //   return dayjs(date).format(TIME_AND_DATE_FORMATS.DAY_MONTH_DATE_YEAR_SHORTED_FORMAT);
  // };

  get dateAsString() {
    if (!this.beginDate || !this.endDate) return null;
    return {
      beginDate: toStringTimeFormatter(this.beginDate),
      endDate: toStringTimeFormatter(this.endDate)
    };
  }

  @action.bound async getGraphReport(date: Array<any>, buildingId: number) {
    const [dateStart, dateEnd] = date;
    if (!dateStart) return notification.warning({ message: 'Please select the begin date!' });
    if (!dateEnd) return notification.warning({ message: 'Please select the end date!' });
    if (dateStart > dateEnd) return notification.warning({ message: 'End date must be bigger than start date!' });

    try {
      this.loading = true;

      // manually set end of day in Dubai time
      this.buildingId = buildingId;
      this.beginDate = dayjs(dateStart).utcOffset(this.dubaiTZ).startOf('day').valueOf();
      this.endDate = dayjs(dateEnd).utcOffset(this.dubaiTZ).endOf('day').valueOf();
      this.isOneWeekGraphPeriod = dayjs(this.endDate).diff(this.beginDate, 'days') <= 7;
      const reportParams = { beginDate: this.beginDate, endDate: this.endDate, buildingId };

      const promises = [
        api.getRoomsReport(reportParams),
        ...(this.isOneWeekGraphPeriod ? [api.getGraphReport(reportParams)] : [])
      ];

      const [roomsData, graphData] = await Promise.all(promises);

      this.report = { ...roomsData, ...graphData };
    } catch (error) {
      notification.error({
        message: error.message
      });
    } finally {
      this.loading = false;
    }
  }

  @action.bound async getRoomDataById() {
    try {
      this.actionLoading = true;
      this.roomData!.roomInfo = await api.getRoomDataById(this.selectedRoomId);
    } catch (error) {
      notification.error({
        message: error.message
      });
    } finally {
      this.actionLoading = false;
    }
  }

  @action.bound async getRoomAlarmsById(roomId?: string) {
    try {
      this.actionLoading = true;

      const alarms = await api.getAlarmsDataByDate(roomId || this.selectedRoomId);

      const sorterAlarms = alarms.sort((a, b) => a.timestamp > b.timestamp);

      this.roomData = {
        ...this.roomData,
        alarms: sorterAlarms
      };
    } catch (error) {
      notification.error({
        message: error.message
      });
    } finally {
      this.actionLoading = false;
    }
  }

  @action.bound async getRoomMeasurementsById(id: string) {
    if (!id) {
      return notification.warning({ message: 'Room id is missing' });
    }
    try {
      this.actionLoading = true;
      this.selectedRoomId = id;
      const measurements = await api.getRoomMeasurements10MinGranularityById(id, this.beginDate!, this.endDate!);

      this.roomData = {
        ...this.roomData,
        measurements: measurements.map((item: MeasurementsInterface) => ({
          ...item,
          temp: item.roomTemp
        }))
      };
    } catch (error) {
      console.log({ error });
      notification.error({
        message: error.message
      });
    } finally {
      this.actionLoading = false;
    }
  }

  @action.bound clearReportStore() {
    this.beginDate = null;
    this.endDate = null;
    this.report = null;
    this.roomData = null;
    this.buildingId = null;
  }
}
